import { AioExternalSingleWithSource, eSingleSource } from "contexts/hierarchy/interface";
import { IEditorV3, stringToV3 } from "editor-v3";
import { DomainReplacements } from "functions/api/hierarchy/DomainReplacements";
import { eHierarchyLevel, eReleaseStatus } from "../../functions/api/enums";

/**
 * Merges the provided external singles with the parent external singles, ensuring no duplicates.
 *
 * @param tocNumber - The TOC number used for report replacements.
 * @param releaseStatus - Release status governing which singles are applicable.
 * @param domain - Domain used to filter domain replacements.
 * @param tlfSingles - The external singles defined on the TLF item.
 * @param level - The hierarchy level for which to collect singles.
 * @param hierarchySingles - The external singles defined on this hierarchy level.
 * @param domainReplacements - Domain replacement entries for this level.
 * @param parentSingles - External singles inherited from parent hierarchies.
 * @param parentDomainReplacements - Domain replacements inherited from parent hierarchies.
 * @returns The merged list of external singles to apply for the current context.
 */
export const getExternalSingles = (
  tocNumber: string = "",
  releaseStatus: eReleaseStatus = eReleaseStatus.Draft,
  domain: string = "",
  tlfSingles: AioExternalSingleWithSource<IEditorV3>[] = [],
  level: eHierarchyLevel = eHierarchyLevel.Corporate,
  hierarchySingles: AioExternalSingleWithSource<IEditorV3>[] = [],
  domainReplacements: DomainReplacements[] = [],
  parentSingles: AioExternalSingleWithSource<IEditorV3>[] = [],
  parentDomainReplacements: DomainReplacements[] = [],
): AioExternalSingleWithSource<IEditorV3>[] => {
  const es = [
    // Add ToC replacement for reports
    ...(level === eHierarchyLevel.Report && tocNumber
      ? [
          {
            airid: "toc",
            oldText: "!!toc!!",
            newText: stringToV3(tocNumber),
            source: eSingleSource.tlfItem,
          },
        ]
      : []),
    // Add TLF-specific singles
    ...tlfSingles,
    // Add domain replacements for draft templates (release pending should have had them applied)
    ...(level !== eHierarchyLevel.Report && [eReleaseStatus.Draft].includes(releaseStatus)
      ? [
          ...domainReplacements
            .filter((dr) => dr.domain === domain)
            .map((dr) => ({
              airid: dr.airid,
              oldText: dr.oldText,
              newText: dr.newText,
              source: eSingleSource.domain,
            })),
          ...parentDomainReplacements
            .filter((dr) => dr.domain === domain)
            .map((dr) => ({
              airid: dr.airid,
              oldText: dr.oldText,
              newText: dr.newText,
              source: eSingleSource.domain,
            })),
        ]
      : []),
    // Add normal singles for reports or drafts
    ...(level === eHierarchyLevel.Report ||
    [eReleaseStatus.ReleasePending, eReleaseStatus.Draft].includes(releaseStatus)
      ? [...hierarchySingles, ...parentSingles]
      : []),
  ];
  return es;
};
